import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/components/toast/useToast';
import { salesContactsApi } from '../api/salesContactsApi';
import type {
  CreateSalesContactActivityPayload,
  CreateSalesContactPayload,
  SalesContact,
  SalesContactActivity,
  SalesContactDetail,
  SalesContactQuery,
  UpdateSalesContactPayload,
} from '../types';

const KEY = 'sales-contacts';

/** Nest validation errors arrive as `message: string[]`; take the first line. */
const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

export const useSalesContacts = (query: SalesContactQuery = {}) =>
  useQuery<SalesContact[]>({
    queryKey: [KEY, 'list', query],
    queryFn: () => salesContactsApi.list(query),
    staleTime: 30 * 1000,
  });

export const useSalesContact = (id: string | undefined) =>
  useQuery<SalesContactDetail>({
    queryKey: [KEY, 'detail', id],
    queryFn: () => salesContactsApi.get(id as string),
    enabled: !!id,
  });

/**
 * The activity timeline. `GET /sales/contacts/:id` already eager-loads activities, but the
 * detail query is also invalidated by every field edit — keeping the timeline on its own key
 * means adding a note refreshes the list without refetching the whole contact, and vice versa.
 */
export const useSalesContactActivities = (id: string | undefined) =>
  useQuery<SalesContactActivity[]>({
    queryKey: [KEY, 'activities', id],
    queryFn: () => salesContactsApi.listActivities(id as string),
    enabled: !!id,
  });

export const useCreateSalesContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (payload: CreateSalesContactPayload) => salesContactsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
      toast.success('Contact created');
    },
    onError: (err: unknown) => {
      // `email` and `igHandle` are unique across contacts — a 409 deserves a plainer sentence
      // than whatever the database constraint produced.
      if ((err as { status?: number })?.status === 409) {
        toast.error('A contact with that email or Instagram handle already exists.');
        return;
      }
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error(errMsg(err, 'Failed to create contact'));
      }
    },
  });
};

export const useUpdateSalesContact = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSalesContactPayload }) =>
      salesContactsApi.update(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', variables.id] });
      // A stage change auto-appends a STAGE_CHANGE activity server-side, so an open timeline
      // is stale the moment any update lands — not only a note.
      queryClient.invalidateQueries({ queryKey: [KEY, 'activities', variables.id] });
      toast.success('Contact updated');
    },
    onError: (err: unknown) => {
      if ((err as { status?: number })?.status === 409) {
        toast.error('A contact with that email or Instagram handle already exists.');
        return;
      }
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error(errMsg(err, 'Failed to update contact'));
      }
    },
  });
};

export const useAddSalesContactActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateSalesContactActivityPayload }) =>
      salesContactsApi.addActivity(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'activities', variables.id] });
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', variables.id] });
      toast.success('Note added');
    },
    onError: (err: unknown) => {
      if (!(err as { toasted?: boolean })?.toasted) toast.error(errMsg(err, 'Failed to add note'));
    },
  });
};
